import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Translator, { __ } from "services/Translator";
import api from "services/api";
import { isGameAvailable } from "services/gameAvailability";

const GameItem = ({ game, isAuthenticated, props }) => {
  const imageRef = useRef(null);

  // 1) Start with loader image
  const loaderImage = require("assets/play365/images/games/game-loader.gif");
  // 2) Fallback image (same file as loader, per request)
  const defaultImage = loaderImage;

  // The <img> element's current src
  const [currentSrc, setCurrentSrc] = useState(loaderImage);
  // Whether we've successfully swapped to the real image at least once
  const [processed, setProcessed] = useState(false);

  const [isFavorite, setIsFavorite] = useState(game.is_favorite_game);

  const available = isGameAvailable(game);

  // Maintain 1.31 height/width ratio
  const applyFixedRatio = () => {
    if (!imageRef.current) return;
    const img = imageRef.current;
    img.style.height = "auto";
    img.height = img.width * 1.31;
  };

  // Throttled resize handler (attach only when processed)
  const handleResize = () => {
    if (!processed) return;
    if (!window.__gameImgResizeThrottle) {
      window.__gameImgResizeThrottle = setTimeout(() => {
        applyFixedRatio();
        window.__gameImgResizeThrottle = null;
      }, 16); // ~60fps
    }
  };

  // Preload the real image whenever game changes
  useEffect(() => {
    let cancelled = false;

    // Show loader first
    setCurrentSrc(loaderImage);
    setProcessed(false);

    const targetUrl = game?.game_snapshot_url || "";

    if (!targetUrl) {
      // No URL → fallback immediately
      setCurrentSrc(defaultImage);
      return;
    }

    const preloader = new Image();
    preloader.onload = () => {
      if (cancelled) return;
      // Swap to real image
      setCurrentSrc(targetUrl);
      setProcessed(true);
      // Apply ratio after swap
      setTimeout(applyFixedRatio, 0);
    };
    preloader.onerror = () => {
      if (cancelled) return;
      // Fallback to default
      setCurrentSrc(defaultImage);
      setProcessed(false);
      setTimeout(applyFixedRatio, 0);
    };
    preloader.src = targetUrl;

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.id, game.game_snapshot_url]);

  // Ensure ratio when <img> actually loads (loader/real/fallback)
  const handleImgLoad = () => {
    applyFixedRatio();
  };

  // Safety net: if actual element load fails later, fallback to default
  const handleImgError = () => {
    if (currentSrc !== defaultImage) {
      setCurrentSrc(defaultImage);
      setProcessed(false);
      setTimeout(applyFixedRatio, 0);
    }
  };

  // Add/remove resize listener while showing the real image
  useEffect(() => {
    if (processed) {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [processed]);

  const toggleFavoriteGame = async () => {
    const response = await api.post("/toggle-favorite-game", { gameId: game.id });
    if (response.status === 200) {
      setIsFavorite(response.data.status === "added");
    }
  };

  return (
    <div className={"gameItemBlock " + (available ? "" : "gameItemBlock--unavailable")}>
      <div className="game_thumb">
        {available ? (
          <Link hrefLang={Translator.getSelectedLanguage().code} to={"/play/" + game.slug}>
            <img
              ref={imageRef}
              src={currentSrc}
              onLoad={handleImgLoad}
              onError={handleImgError}
              alt={game.game_name ?? ""}
            />
          </Link>
        ) : (
          // Structural swap, not pointer-events: a <Link> still routes on Enter and stays tabbable.
          <div className="game_thumb_link game_thumb_link--locked">
            <img
              ref={imageRef}
              src={currentSrc}
              onLoad={handleImgLoad}
              onError={handleImgError}
              alt={game.game_name ?? ""}
            />
          </div>
        )}

        {/* Sibling of .game_overlay on purpose: .game_overlay is display:none until :hover, so it never shows on touch. */}
        {!available && (
          <div
            className="gameItemUnavailable"
            role="note"
            aria-label={__("This game isn't available in your region or currency.")}
          >
            <span className="gameItemUnavailable__icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </span>
            <span className="gameItemUnavailable__copy">
              {__("This game isn't available in your region or currency.")}
            </span>
          </div>
        )}

        <div className="game_overlay">
          {!!game.rtp && (
            <div className="rtp_info d-none">
              <div className="left rtp_text">
                RTP: <span>{game.rtp}%</span>
              </div>
            </div>
          )}

          <h3 className="game_name">{game.game_name}</h3>

          <div className="">
            {!available ? null : !isAuthenticated ? (
              <Link
                hreflang={Translator.getSelectedLanguage().code}
                onClick={() => {
                  props.setModalSignUpPropsData(true);
                  setTimeout(() => {
                    props.setModalSignUpPropsData(false);
                  }, 500);
                }}
                className="game_action"
              >
                {/* Play CTA SVG (unchanged) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xlink="http://www.w3.org/1999/xlink"
                  width="50.637"
                  height="52"
                  viewBox="0 0 50.637 52"
                >
                  <defs>
                    <filter id="Polygon_36" x="7" y="4" width="43.636" height="48" filterUnits="userSpaceOnUse">
                      <feOffset dy="3" input="SourceAlpha" />
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feFlood flood-color="#fff" flood-opacity="0.322" />
                      <feComposite operator="in" in2="blur" />
                      <feComposite in="SourceGraphic" />
                    </filter>
                  </defs>
                  <g id="Group_1545" data-name="Group 1545" transform="translate(-792 -546)">
                    <g
                      id="Ellipse_251"
                      data-name="Ellipse 251"
                      transform="translate(792 546)"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="5"
                    >
                      <circle cx="25" cy="25" r="25" stroke="none" />
                      <circle cx="25" cy="25" r="22.5" fill="none" />
                    </g>
                    <g transform="matrix(1, 0, 0, 1, 792, 546)" filter="url(#Polygon_36)">
                      <path
                        id="Polygon_36-2"
                        data-name="Polygon 36"
                        d="M11.548,5.9a4,4,0,0,1,6.9,0l8.025,13.716a4,4,0,0,1-3.452,6.02H6.975a4,4,0,0,1-3.452-6.02Z"
                        transform="translate(41.64 10) rotate(90)"
                        fill="#fff"
                      />
                    </g>
                  </g>
                </svg>
              </Link>
            ) : (
              <Link hreflang={Translator.getSelectedLanguage().code} className="game_action" to={"/play/" + game.slug}>
                {/* Play CTA SVG (unchanged) */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xlink="http://www.w3.org/1999/xlink"
                  width="50.637"
                  height="52"
                  viewBox="0 0 50.637 52"
                >
                  <defs>
                    <filter id="Polygon_36" x="7" y="4" width="43.636" height="48" filterUnits="userSpaceOnUse">
                      <feOffset dy="3" input="SourceAlpha" />
                      <feGaussianBlur stdDeviation="3" result="blur" />
                      <feFlood flood-color="#fff" flood-opacity="0.322" />
                      <feComposite operator="in" in2="blur" />
                      <feComposite in="SourceGraphic" />
                    </filter>
                  </defs>
                  <g id="Group_1545" data-name="Group 1545" transform="translate(-792 -546)">
                    <g
                      id="Ellipse_251"
                      data-name="Ellipse 251"
                      transform="translate(792 546)"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="5"
                    >
                      <circle cx="25" cy="25" r="25" stroke="none" />
                      <circle cx="25" cy="25" r="22.5" fill="none" />
                    </g>
                    <g transform="matrix(1, 0, 0, 1, 792, 546)" filter="url(#Polygon_36)">
                      <path
                        id="Polygon_36-2"
                        data-name="Polygon 36"
                        d="M11.548,5.9a4,4,0,0,1,6.9,0l8.025,13.716a4,4,0,0,1-3.452,6.02H6.975a4,4,0,0,1-3.452-6.02Z"
                        transform="translate(41.64 10) rotate(90)"
                        fill="#fff"
                      />
                    </g>
                  </g>
                </svg>
              </Link>
            )}
          </div>

          <div className="game_info">
            <div className="left">
              <h4 className="game_provider_name">{game.provider?.name}</h4>
            </div>
            <div className="right">
              {!isAuthenticated ? (
                <Link
                  hreflang={Translator.getSelectedLanguage().code}
                  onClick={() => {
                    props.setModalSignUpPropsData(true);
                    setTimeout(() => {
                      props.setModalSignUpPropsData(false);
                    }, 500);
                  }}
                  className="favorite_icon"
                >
                  {/* Heart outline (unchanged) */}
                  <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_2081_1449)">
                      <path
                        d="M5.4 0C2.4 0 0 2.4 0 5.4C0 6.4 0.5 7.3 0.9 7.9C1.3 8.5 1.8 8.9 1.8 8.9L9.5 16.6L10 17.1L10.5 16.6L18.2 9C18.2 9 20 7.4 20 5.4C20 2.4 17.6 0 14.6 0C12.1 0 10.6 1.5 10 2.1C9.4 1.5 7.8 0 5.4 0ZM5.4 1.4C7.5 1.4 9.5 3.5 9.5 3.5L10 4.1L10.5 3.5C10.5 3.5 12.5 1.4 14.6 1.4C16.8 1.4 18.5 3.2 18.5 5.3C18.5 6.4 17.2 7.9 17.2 7.9L10 15.2L2.8 7.9C2.8 7.9 2.5 7.6 2.1 7.1C1.7 6.6 1.4 5.9 1.4 5.4C1.4 3.2 3.2 1.4 5.4 1.4Z"
                        fill="#4698ED"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2081_1449">
                        <rect width="20" height="17.2" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </Link>
              ) : (
                <Link
                  hreflang={Translator.getSelectedLanguage().code}
                  onClick={toggleFavoriteGame}
                  className="favorite_icon"
                >
                  {isFavorite ? (
                    // Filled heart
                    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2081_3472)">
                        <path
                          d="M14.6 0C12.5 0 10.8 1.3 10 2C9.2 1.3 7.4 0 5.4 0C2.4 0 0 2.4 0 5.4C0 7.2 1.7 8.9 1.8 9L10 17.2L18.2 9C18.3 8.9 20 7.2 20 5.4C20 2.4 17.6 0 14.6 0Z"
                          fill="#4698ED"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2081_3472">
                          <rect width="20" height="17.2" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  ) : (
                    // Outline heart
                    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g clipPath="url(#clip0_2081_1449)">
                        <path
                          d="M5.4 0C2.4 0 0 2.4 0 5.4C0 6.4 0.5 7.3 0.9 7.9C1.3 8.5 1.8 8.9 1.8 8.9L9.5 16.6L10 17.1L10.5 16.6L18.2 9C18.2 9 20 7.4 20 5.4C20 2.4 17.6 0 14.6 0C12.1 0 10.6 1.5 10 2.1C9.4 1.5 7.8 0 5.4 0ZM5.4 1.4C7.5 1.4 9.5 3.5 9.5 3.5L10 4.1L10.5 3.5C10.5 3.5 12.5 1.4 14.6 1.4C16.8 1.4 18.5 3.2 18.5 5.3C18.5 6.4 17.2 7.9 17.2 7.9L10 15.2L2.8 7.9C2.8 7.9 2.5 7.6 2.1 7.1C1.7 6.6 1.4 5.9 1.4 5.4C1.4 3.2 3.2 1.4 5.4 1.4Z"
                          fill="#4698ED"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_2081_1449">
                          <rect width="20" height="17.2" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  )}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameItem;
